import React, { useEffect, useState } from "react";
import { Col, Divider, Row, Select } from "antd";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import styles from "./add-edit-school-subjects.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import { toast } from "react-toastify";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import CustomUpload from "../../../../../../../../../common/CustomUpload/CustomUpload";
import CustomCheckBox from "../../../../../../../../../common/custom-checkbox/custom-checkbox";
import { PlusOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import {
  addSubjects,
  fetchSubjectTypes,
  getParents,
  updateSubject,
} from "../../../../../../../../../services/subject";
import {
  getImageName,
  refetchSchool,
} from "../../../../../../../../../utils/common";
import SVGIcon from "../../../../../../../../../constants/SVGIcon";

const { Option } = Select;

const defaultFormValues = {
  name: "",
  subject_logo: "",
  department: "",
  is_sub: false,
  subSubjects: [],
  level: "",
};

const AddEditSchoolSubjects = ({ open, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [levels, setLevels] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);

  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subSubjects",
  });
  const isSubChecked = useWatch({
    control,
    name: "is_sub",
  });
  useEffect(() => {
    if (!open) return;
    getDepartments();
  }, [open]);

  useEffect(() => {
    if (!isSubChecked) {
      const subSubjects = getValues("subSubjects") || [];
      const idsToDelete = subSubjects?.map((s) => s?.id)?.filter(Boolean);
      setDeletedIds(idsToDelete);
      remove();
    }
  }, [isSubChecked]);

  useEffect(() => {
    if (data?.id) {
      reset({
        name: data?.name || "",
        subject_logo: getImageName(data?.image_url) || "",
        department: data?.department_id || "",
        is_sub:
          Array.isArray(data?.subSubjects) && data?.subSubjects.length > 0,
        subSubjects: Array.isArray(data?.subSubjects)
          ? data.subSubjects.map((s) => ({
              subSubjectName: s?.name
                ?.replace(/^\[\"?/, "")
                ?.replace(/\"?\]$/, ""),
              id: s.id,
            }))
          : [],
        level: data?.parent_subject_id || "",
      });
    } else {
      reset(defaultFormValues);
    }
  }, [data, reset]);

  const getDepartments = async () => {
    try {
      setLoading(true);
      const response = await fetchSubjectTypes();
      if (response?.data) {
        const data = response?.data?.data?.departments;
        setDepartmentList(data);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!data?.parent_subject_id) return;

    const fetchAllParentsSubject = async () => {
      try {
        setLoading(true);
        const response = await getParents();

        const resData = response?.data?.data?.subjects || [];

        const formatted = resData.map((item, index) => ({
          label: item.name,
          value: item.id,
        }));

        setLevels(formatted);
      } catch (err) {
        console.error("Failed to fetch parent subjects:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllParentsSubject();
  }, [data?.parent_subject_id]);

  const handleParentUpdation = (formValues) => {
    const subSubjectsPayload = { insert: [], delete: [] };

    formValues.subSubjects?.forEach((s) => {
      if (!s.id && s.subSubjectName?.trim()) {
        subSubjectsPayload.insert.push(s.subSubjectName.trim());
      }
    });

    if (deletedIds.length > 0) {
      subSubjectsPayload.delete.push(...deletedIds);
    }

    return subSubjectsPayload;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }
      const formValues = getValues();
      const formData = new FormData();

      formData.append("name", formValues.name);
      formData.append("department_id", formValues.department);
      if (formValues.subject_logo?.size) {
        formData.append("image_url", formValues.subject_logo);
      }
      if (formValues.level) {
        formData.append("parent_subject_id", formValues.level);
      }
      if (data?.id) {
        const subSubjectsPayload = handleParentUpdation(formValues);
        formData.append("sub_subjects", JSON.stringify(subSubjectsPayload));
        await updateSubject(data?.id, formData);
      } else {
        if (formValues.is_sub && formValues.subSubjects?.length > 0) {
          const subSubjectsArray = formValues.subSubjects.map(
            (s) => s.subSubjectName
          );
          formData.append("sub_subjects", subSubjectsArray.join(","));
        }
        await addSubjects(formData);
      }
      toast.success(`Subject ${data?.id ? "edited" : "created"} successfully!`);
      refetchSchool();
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message ||
          `Failed to ${data?.id ? "edit" : "create"} subject`
      );
    } finally {
      setDeletedIds([]);
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset(defaultFormValues);
    setDeletedIds([]);
    onClose();
  };

  return (
    <CustomDrawer
      title={`${data?.id ? "Edit" : "Add New"} Subject`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add New"} Subject`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="40vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Spin spinning={loading}>
        <Row gutter={[0, 20]}>
          <Col span={24}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="name"
                  label="Subject Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter Subject Name"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={24}>
            <Controller
              name="subject_logo"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomUpload
                  accepts=".jpg, .png, .pdf"
                  error={error}
                  fileName={value?.name}
                  handleBeforeUpload={(file) => {
                    onChange(file);
                    return false;
                  }}
                  label="Upload Subject Logo"
                  showOptional="(jpg, png, pdf)"
                />
              )}
            />
          </Col>
          <Col span={24}>
            <Controller
              name="department"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id="department"
                  label="Departments"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  loading={loading}
                >
                  {departmentList?.map((option, index) => (
                    <Option key={`${option?.id}-${index}`} value={option?.id}>
                      {option?.name}
                    </Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
          {data?.parent_subject_id && (
            <Col span={24}>
              <label
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  display: "inline-block",
                }}
              >
                Select Parent Subject
              </label>
              <Controller
                name="level"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelColor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    value={value}
                    onChange={(val) => onChange(val)}
                    status={error ? "error" : ""}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {levels?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>
          )}
          {(!data || data.parent_subject_id === null) && (
            <>
              <Col span={24}>
                <label
                  style={{
                    fontWeight: 500,
                    marginBottom: 12,
                    display: "inline-block",
                  }}
                >
                  Sub-Subject
                </label>
                <Controller
                  name="is_sub"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomCheckBox
                      id="is_sub"
                      label=" Mark if this subject contains sub-subjects"
                      showOptional="(Leave unchecked if not contain sub-subjects)"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      errorMessage={error?.message}
                    />
                  )}
                />
              </Col>
              {isSubChecked && (
                <Col span={8} className={styles.addSubWrapper}>
                  <CustomButton
                    title="Add Sub-Subject"
                    type="text"
                    icon={
                      <div className={styles.iconCircle}>
                        <PlusOutlined />
                      </div>
                    }
                    className={styles.addSubBtn}
                    showTooltip={false}
                    onClick={() => append({ subSubjectName: "", id: null })}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
        {(!data || data.parent_subject_id === null) && (
          <>
            <Divider style={{ backgroundColor: "#d9d9d9" }} />

            {fields?.map((field, index) => (
              <Row
                gutter={[10, 20]}
                className={styles.subRoomWrapper}
                key={field.id}
              >
                <Controller
                  name={`subSubjects.${index}.id`}
                  control={control}
                  defaultValue={field.id}
                  render={({ field }) => <input type="hidden" {...field} />}
                />

                <Col span={20}>
                  <Controller
                    name={`subSubjects.${index}.subSubjectName`}
                    control={control}
                    rules={{ required: true }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomTextfield
                        id={`subSubjects.${index}.subSubjectName`}
                        showRequired
                        labelColor="#171717"
                        size="large"
                        value={value}
                        placeholder="Enter Sub-Subject Name"
                        onChange={onChange}
                        disabled={!!field.subSubjectName}
                        status={error ? "error" : ""}
                      />
                    )}
                  />
                </Col>

                <Col span={4} className={styles.deleteSubIconWrapper}>
                  <SVGIcon
                    name="delete"
                    width="20"
                    height="20"
                    className={styles.deleteSubIcon}
                    stroke="rgba(243, 17, 17, 1)"
                    onClick={() => {
                      const subSubject = getValues(`subSubjects.${index}`);
                      if (subSubject?.id) {
                        setDeletedIds((prev) => [...prev, subSubject.id]);
                      }
                      remove(index);
                    }}
                  />
                </Col>
              </Row>
            ))}
          </>
        )}
      </Spin>
    </CustomDrawer>
  );
};

export default AddEditSchoolSubjects;
